import Web3 from 'web3';
import { AbiItem } from 'web3-utils';

import { STAKING_TOKEN_ABI } from '../../../../../abi/abi-staking-token';
import { STAKING_TOKEN_COMPOUND_ABI } from '../../../../../abi/abi-staking-token-compound';
import { RPC_BASE } from '../../../../../constants';

/**
 * V2 staking pools for the V1 -> V2 restaking flow.
 *
 * Source: GET `${API_URL}staking-token` (action `listStakingTokenV2` in
 * `storeStaked.js`). Only pools that have NOT reached their `endTime` are shown.
 */

const web3: any = new Web3(RPC_BASE);

const SECONDS_PER_MONTH = 30 * 24 * 60 * 60;

export interface V2Pool {
  poolId: string;
  poolName: string;
  symbol: string;
  /** Duration tier (StakingTokenTypeEnum): 1=3M, 2=6M, 3=12M */
  type: number;
  /** APR/APY in percent, e.g. 27.11 */
  apr: number;
  /** Total value staked (token units) - read on-chain */
  tvl: number;
  /** Lock duration in months, derived from stakeLockPeriod */
  lockMonths: number;
  /** Early withdrawal fee in percent */
  earlyFee: number;
  /** Whether the pool auto-compounds rewards (type 3 pools) */
  autoCompound: boolean;
  poolContract: string;
  tokenContract: string;
}

/** "Chưa đến endTime" - keep pools where now < endTime. */
export const isPoolActiveByEndTime = (endTime?: number) =>
  Number(endTime) * 1000 > Date.now();

/** Map a raw `staking-token` API item to the shape the UI consumes. */
export const mapStakingTokenToV2 = (item: any): V2Pool => {
  // poolClassify: 0 = fixed, 1 = compound. Compound pools expose an APY; fixed
  // pools (and compound pools missing an APY) fall back to the plain APR.
  const isCompound = item?.poolClassify === 1 || item?.compoundInterval != null;
  const rate =
    isCompound && Number(item?.stakingAPY) > 0
      ? Number(item.stakingAPY)
      : Number(item?.stakingAPR || 0);

  return {
    poolId: item?._id,
    poolName: item?.name,
    symbol: item?.tokenInfo?.tokenSymbol || 'MEY',
    type: Number(item?.type),
    apr: rate / 100,
    tvl: 0,
    lockMonths: Math.round(Number(item?.stakeLockPeriod || 0) / SECONDS_PER_MONTH),
    earlyFee: Number(item?.earlyUnstakeFee || 0) / 100,
    autoCompound: isCompound,
    poolContract: item?.contractAddress,
    tokenContract: item?.tokenInfo?.contractAddress,
  };
};

/**
 * Read the total staked amount of a V2 pool on-chain via `totalStaked()`
 * (best effort). Uses the compound ABI for compound pools, the fixed ABI
 * otherwise (`src/abi/abi-staking-token*.ts`).
 */
export const readPoolTvl = async (
  poolContract?: string,
  isCompound?: boolean
): Promise<number> => {
  if (!poolContract) return 0;
  try {
    const abi = isCompound ? STAKING_TOKEN_COMPOUND_ABI : STAKING_TOKEN_ABI;
    const contract = new web3.eth.Contract(
      abi as unknown as AbiItem,
      poolContract
    );
    const total = await contract.methods.totalStaked().call();
    return Number(web3.utils.fromWei(total || '0', 'ether'));
  } catch {
    return 0;
  }
};
